import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { useMemo } from "react";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { InteractionType } from "@/common/types";

export const useSupervisor = (): boolean => {
	const hasSupervisorRights = useAgentPermissionsStore(
		(state: AgentPermissionsState) =>
			state.isSupervisor ||
			(state.hasOperatorRights && state.hasAllInteractionVisibilityRight)
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const isAllInteractionsTabVisibile = useMemo<boolean>(() => {
		if (!hasSupervisorRights) {
			return false;
		}

		return (
			queues.filter(
				(queue) =>
					queue.isSupervisor ||
					(queue.isOperator && queue.hasAllInteractionVisibilityRight)
			).length > 0
		);
	}, [hasSupervisorRights, queues]);

	return isAllInteractionsTabVisibile;
};

export const useTextMonitoring = (): boolean => {
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const isTextMonitoring = useMemo(() => {
		const userId = Lockr.get(StorageKeys.UserId);

		return (
			(currentInteraction?.type === InteractionType.Email ||
				currentInteraction?.type === InteractionType.WhatsApp ||
				currentInteraction?.type === InteractionType.Webchat) &&
			currentInteraction.monitoringSupervisorId === userId
		);
	}, [currentInteraction]);

	return isTextMonitoring;
};

export const useQueueSupervisorOperator = (): boolean => {
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const isQueueSupervisorOrOperator = useMemo(
		() => queues.some((q) => q.isSupervisor || q.isOperator) ?? [
			queues,
			currentInteraction,
		],
		[queues, currentInteraction]
	);

	return isQueueSupervisorOrOperator;
};
